import { Link, type Href } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, View, type TextStyle } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { listModerationCases, type ModerationCaseStatus, type ModerationCaseSummary } from "@/src/control/api";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import { formatControlDate, moderationCaseStatusLabel, moderationReasonLabel, moderationSeverityLabel, moderationSourceLabel, moderationTargetLabel } from "@/src/control/presentation";

const breakableTextStyle: TextStyle | undefined = Platform.OS === "web"
  ? ({ overflowWrap: "anywhere", wordBreak: "break-word" } as unknown as TextStyle)
  : undefined;

const statuses: { value: ModerationCaseStatus; label: string }[] = [
  { value: "open", label: "Ouverts" },
  { value: "reviewing", label: "En cours" },
  { value: "resolved", label: "Résolus" },
  { value: "dismissed", label: "Classés" },
];

export default function ControlModerationScreen() {
  return <ControlAccessGate permission="moderation.read"><ModerationQueue /></ControlAccessGate>;
}

function ModerationQueue() {
  const [status, setStatus] = useState<ModerationCaseStatus>("open");
  const [items, setItems] = useState<ModerationCaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    listModerationCases(status, 50)
      .then((value) => active && setItems(value))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, status]);

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.header}>
        <Text style={styles.kicker}>MODÉRATION</Text>
        <Text style={styles.title}>File de modération</Text>
        <Text style={styles.subtitle}>Priorise les signalements et prends une décision sur les contenus qui nécessitent un examen humain.</Text>
      </View>

      <ScrollView horizontal style={styles.filterRail} contentContainerStyle={styles.filters} showsHorizontalScrollIndicator={false}>
        {statuses.map((item) => (
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: item.value === status }}
            key={item.value}
            onPress={() => setStatus(item.value)}
            style={[styles.filter, item.value === status && styles.filterActive]}
          >
            <Text style={[styles.filterText, item.value === status && styles.filterTextActive]}>{item.label}</Text>
          </Pressable>
        ))}
      </ScrollView>

      {!loading && !error ? <Text style={styles.count}>{items.length} dossier{items.length === 1 ? "" : "s"} affiché{items.length === 1 ? "" : "s"}</Text> : null}
      {loading ? <ActivityIndicator color={text.brand} /> : null}
      {error ? <Pressable accessibilityRole="button" onPress={refresh} style={styles.state}><Text style={styles.stateTitle}>File indisponible</Text><Text style={styles.stateBody}>Impossible de charger les dossiers. Réessayer.</Text></Pressable> : null}
      {!loading && !error && !items.length ? <Text style={styles.empty}>Aucun dossier dans cet état.</Text> : null}

      {!loading && !error ? items.map((item) => <CaseRow item={item} key={item.id} />) : null}
    </ScrollView>
  );
}

function CaseRow({ item }: { item: ModerationCaseSummary }) {
  const author = item.author?.displayName || (item.author?.username ? `@${item.author.username.replace(/^@/, "")}` : "Auteur non renseigné");
  const reporter = item.reporter?.displayName || (item.reporter?.username ? `@${item.reporter.username.replace(/^@/, "")}` : "Reporter non renseigné");
  const reason = item.reason || item.autoModerationResult?.reasonCodes?.[0];
  const date = formatControlDate(item.latestReportCreatedAt || item.updatedAt || item.createdAt);
  return (
    <Link href={`/control/moderation/${item.id}` as Href} asChild>
      <Pressable accessibilityRole="link" style={styles.row}>
        <View style={styles.rowHeader}>
          <View style={styles.rowPrimary}>
            <Text style={[styles.rowTitle, breakableTextStyle]}>{moderationReasonLabel(reason)}</Text>
            <Text style={[styles.target, breakableTextStyle]}>{moderationTargetLabel(item.targetType, item.reportedIdentityType)}</Text>
          </View>
          <StatusBadge status={item.status} />
        </View>
        <View style={styles.metaRow}>
          <Text style={[styles.meta, breakableTextStyle]}>Auteur : {author}</Text>
          <SeverityBadge severity={item.severity} />
          {item.source ? <Text style={styles.sourceBadge}>{moderationSourceLabel(item.source)}</Text> : null}
        </View>
        <Text style={[styles.meta, breakableTextStyle]}>Dernier signalement : {reporter}</Text>
        {item.details ? <Text numberOfLines={3} style={[styles.details, breakableTextStyle]}>{item.details}</Text> : <Text style={styles.detailsPlaceholder}>Aucun détail supplémentaire.</Text>}
        <View style={styles.footer}>
          <Text style={[styles.footerText, breakableTextStyle]}>{date} · {item.reportCount ?? 0} signalement{item.reportCount === 1 ? "" : "s"}</Text>
          <Text style={styles.open}>Ouvrir →</Text>
        </View>
      </Pressable>
    </Link>
  );
}

function StatusBadge({ status }: { status: ModerationCaseStatus }) {
  return <Text style={[styles.badge, status === "dismissed" ? styles.badgeMuted : status === "resolved" ? styles.badgeSuccess : styles.badgeActive]}>{moderationCaseStatusLabel(status)}</Text>;
}

function SeverityBadge({ severity }: { severity?: string }) {
  const value = severity || "normal";
  return <Text style={[styles.severity, value === "critical" ? styles.severityCritical : value === "high" ? styles.severityHigh : styles.severityNormal]}>{moderationSeverityLabel(value)}</Text>;
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  header: { gap: spacing.sm },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 30, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 23, minWidth: 0 },
  filterRail: { flexGrow: 0, maxWidth: "100%", width: "100%" },
  filters: { gap: spacing.sm },
  filter: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, minHeight: 44, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  filterActive: { backgroundColor: surface.selected, borderColor: border.brand },
  filterText: { color: text.secondary, fontWeight: "700" },
  filterTextActive: { color: text.brand },
  count: { color: text.muted, fontSize: 12 },
  state: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.xs, padding: spacing.lg },
  stateTitle: { color: text.primary, fontSize: 16, fontWeight: "800" },
  stateBody: { color: text.secondary },
  empty: { color: text.secondary, paddingVertical: spacing.lg },
  row: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.sm, minWidth: 0, padding: spacing.lg },
  rowHeader: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  rowPrimary: { flex: 1, flexShrink: 1, gap: spacing.xs, minWidth: 0 },
  rowTitle: { color: text.primary, fontSize: 16, fontWeight: "800", minWidth: 0 },
  target: { color: text.brand, fontWeight: "700", minWidth: 0 },
  metaRow: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, minWidth: 0 },
  meta: { color: text.secondary, flexShrink: 1, minWidth: 0 },
  sourceBadge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.secondary, fontSize: 11, fontWeight: "700", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  details: { color: text.primary, lineHeight: 20, minWidth: 0 },
  detailsPlaceholder: { color: text.muted, fontSize: 12 },
  footer: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "space-between", minWidth: 0, paddingTop: spacing.xs },
  footerText: { color: text.muted, flex: 1, fontSize: 12, minWidth: 0 },
  open: { color: text.brand, fontSize: 12, fontWeight: "800" },
  badge: { borderRadius: radius.pill, fontSize: 12, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  badgeActive: { backgroundColor: surface.warning, color: text.brand },
  badgeSuccess: { backgroundColor: surface.success, color: text.info },
  badgeMuted: { backgroundColor: surface.selected, color: text.muted },
  severity: { borderRadius: radius.pill, fontSize: 11, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  severityNormal: { backgroundColor: surface.selected, color: text.muted },
  severityHigh: { backgroundColor: surface.warning, color: text.brand },
  severityCritical: { backgroundColor: surface.danger, color: text.danger },
});
